import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import LoaderOverlay from "../../layout/components/LoaderOverlay";
import BookLocationHeader from "../common/util/BookLocationHeader";
import PersonalDataStep from "./PersonalDataStep";
import TenantLogo from "../../layout/components/TenantLogo";
import BookLocationNavigateNextAction from "../common/util/BookLocationNavigateNextAction";
import BookLocationNavigateBackAction from "../common/util/BookLocationNavigateBackAction";
import GeneralDialog from "../../common/GeneralDialog";
import { Box } from "@mui/material";
import { RootState } from "../../../redux/store";
import { useFetchGroups } from "../../../redux/flows/fetchGroupsFlow";
import GTM from "../../../util/gtm";
import {
  bookingCreationCompleted,
  bookingFlowChanged,
  bookingStepChanged,
  navigatedToNextStep,
  setHasSelectedLocation,
  setSelectedLocation,
  setUseCase,
  dateSelectedClear,
  durationSelectedClear,
} from "../../../redux/slice/bookingCreation/bookingCreationSlice";
import { styled } from "@mui/system";
import LocationSelectionStep from "./LocationSelectionStep";
import HintsStep from "./HintsStep";
import PackageDetailsStep from "./PackageDetailsStep";
import BookingSummaryStep from "./BookingSummaryStep";
import SuccessStep from "./SuccessStep";
import { useCreateNewReservation } from "../../../redux/flows/createParcelCustomerReservationFlow";
import routes from "../../../routes/routes";
import Header from "../../layout/components/Header";
import CloseIcon from "../../../components/icons/CloseIcon";
import { getSvcLocations } from "../../../util/utilFunctions";
import CustomButton from "../../layout/components/CustomButton";
import useServiceStorage from "../../../hooks/useServiceStorage";
import { ServiceTypeSelectorOption } from "../../../types/order";
import { setHasSkippedLockeSelection } from "../../../redux/slice/booking/bookingSlice";

export enum BookingCreationStep {
  hints,
  locationSelection,
  packageDetails,
  personalData,
  bookingSummary,
  success,
}

const Root = styled(Box)(({ theme }) => ({
  padding: `${theme.spacing(1)}px ${theme.spacing(1.5)}px`,
  color: "white",
  height: "100%",
  boxSizing: "border-box",
  backgroundColor: theme.palette.background.default,
  paddingBottom: `calc(${theme.spacing(1)}px + env(safe-area-inset-bottom, 0))`,
  display: "flex",
  flexDirection: "column",
}));

const StepWrapper = styled(Box)(({ theme }) => ({
  flex: 1,
  width: "100%",
  maxWidth: 1000,
  margin: "0 auto",
  overflowY: "auto",
  paddingTop: theme.spacing(3),
  [theme.breakpoints.up("lg")]: {
    paddingTop: theme.spacing(6),
  },
}));

const ActionsWrapper = styled(Box)(({ theme }) => ({
  width: "calc(100% - 42px)",
  maxWidth: 1000,
  margin: "0 auto",
  paddingBottom: theme.spacing(5),
  // position: 'fixed',
  bottom: 10,
  left: 0,
  right: 0,
  [theme.breakpoints.up("lg")]: {
    paddingBottom: theme.spacing(5),
  },
}));

export const LogoBox = styled(Box)(({ theme }) => ({
  width: "150px",
  marginTop: theme.spacing(1),
  position: "absolute",
  left: "50%",
  transform: "translateX(-50%)",
}));

export const CancelButton = styled(Box)({
  boxSizing: "border-box",
  marginLeft: "auto",
});
const BookLocationPage = () => {
  const stepWrapperRef = useRef<HTMLElement>();
  const [pageDialogOpen, setPageDialogOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const step = useSelector((state: RootState) => state.bookingCreation.step);
  const locations = useSelector((state: RootState) => state.booking.locations);
  const { updateService } = useServiceStorage(
    "selectedService",
    ServiceTypeSelectorOption.PARCEL
  );
  const orderData = useSelector(
    (state: RootState) => state.bookingCreation.orderData
  );

  const selectedLocation = useSelector(
    (state: RootState) => state.bookingCreation.selectedLocation
  );
  const { selectedGroup } = useSelector(
    (state: RootState) => state.bookingCreation
  );

  const fetchGroups = useFetchGroups("parcel");

  const createNewReservation = useCreateNewReservation();

  async function createReservation() {
    setLoading(true);
    await createNewReservation();
    setLoading(false);
  }

  function handleSkip() {
    dispatch(setHasSkippedLockeSelection(true));
    dispatch(navigatedToNextStep());
  }

  useEffect(() => {
    updateService(ServiceTypeSelectorOption.PARCEL);

    // dispatch(loadBookingFlow({}));
    stepWrapperRef.current = document.getElementById(
      "parcelBookingProcessStepWrapper"
    )!;
    // @todo body scroll lock breaks the phone number input. Maybe remove it entirely?
    //disableBodyScroll(stepWrapperRef.current);
    //return () => enableBodyScroll(stepWrapperRef.current!);

    // Set the usecase to parcel for this flow
    dispatch(setUseCase({ useCase: "parcel" }));
    dispatch(setHasSkippedLockeSelection(false));

    // Set the bookingFlow
    dispatch(
      bookingFlowChanged({
        bookingFlow: [
          "hints",
          "locationSelection",
          "packageDetails",
          "personalData",
          "bookingSummary",
          "success",
        ],
      })
    );

    // Enable next button for hints step
    if (step === BookingCreationStep.hints) {
      dispatch(
        bookingStepChanged({
          step: BookingCreationStep.hints,
          nextEnabled: true,
        })
      );
    } else if (step === BookingCreationStep.locationSelection) {
      dispatch(
        bookingStepChanged({
          step: BookingCreationStep.locationSelection,
          nextEnabled: !!selectedLocation,
        })
      );
    } else if (step === BookingCreationStep.packageDetails) {
      // Enable next button when both date and package size are selected
      const hasDate = !!orderData.date;
      const hasPackageSize = !!selectedGroup;
      dispatch(
        bookingStepChanged({
          step: BookingCreationStep.packageDetails,
          nextEnabled: hasDate && hasPackageSize,
        })
      );
    }
  }, []);

  // Monitor changes to package details step requirements
  useEffect(() => {
    if (step === BookingCreationStep.packageDetails) {
      const hasDate = !!orderData.date;
      const hasPackageSize = !!selectedGroup;
      dispatch(
        bookingStepChanged({
          step: BookingCreationStep.packageDetails,
          nextEnabled: hasDate && hasPackageSize,
        })
      );
    }
  }, [step, orderData.date, selectedGroup, dispatch]);

  useEffect(() => {
    const manageReduxData = async () => {
      // Handle booking summary step - create reservation
      if (step === BookingCreationStep.packageDetails) {
        // Clean data
        dispatch(dateSelectedClear());
        dispatch(durationSelectedClear());
        setLoading(true);
        await fetchGroups();
        setLoading(false);
      }
      setLoading(false);
      GTM.trackEecCheckoutStep(step);
    };
    manageReduxData();
  }, [step]);

  const headerText = useMemo(() => {
    switch (step) {
      case BookingCreationStep.hints:
        return t("book_location.hints.title");
      case BookingCreationStep.locationSelection:
        return t("book_location.select_location");
      case BookingCreationStep.packageDetails:
        return t("book_location.package_details.title");
      case BookingCreationStep.personalData:
        return t("book_location.personal_data.header_text");
      case BookingCreationStep.bookingSummary:
        return t("book_location.booking_summary.title");
      case BookingCreationStep.success:
        return t("book_location.success.title");

      default:
        return "";
    }
  }, [step, orderData.date]);

  const closePage = () => {
    // history.goBack();
    navigate(routes.home);
    dispatch(bookingCreationCompleted());
  };

  const isLastStep = step === BookingCreationStep.success;
  useEffect(() => {
    if (locations && step === 0) {
      const svcLocations = getSvcLocations(locations, "parcel");
      if (svcLocations.length === 1) {
        dispatch(navigatedToNextStep());
        dispatch(setSelectedLocation(svcLocations[0]));
        dispatch(setHasSelectedLocation(true));
      }
    }
  }, [locations, step]);

  return (
    <Root>
      <Header>
        {step !== BookingCreationStep.success && (
          <div className="h-12 absolute left-2 flex items-center">
            <BookLocationNavigateBackAction />
          </div>
        )}
        <TenantLogo />
        {step !== BookingCreationStep.success && (
          <button
            id="closeButton"
            className="size-12 absolute right-0 text-foreground/90"
            onClick={() => setPageDialogOpen(true)}
          >
            <CloseIcon />
          </button>
        )}
      </Header>
      <Box sx={{ marginTop: "20px" }}>
        <BookLocationHeader text={headerText} />
      </Box>
      <StepWrapper id={"p2pBookingProcessStepWrapper"}>
        {step === BookingCreationStep.hints && <HintsStep />}
        {step === BookingCreationStep.locationSelection && (
          <LocationSelectionStep />
        )}
        {step === BookingCreationStep.packageDetails && <PackageDetailsStep />}
        {step === BookingCreationStep.personalData && <PersonalDataStep />}
        {step === BookingCreationStep.bookingSummary && <BookingSummaryStep />}
        {step === BookingCreationStep.success && <SuccessStep />}
      </StepWrapper>
      <ActionsWrapper>
        {step === BookingCreationStep.packageDetails && (
          <CustomButton
            isButtonDark={false}
            variant={"outlined"}
            isOutlined={true}
            onClick={handleSkip}
            fullWidth={true}
            style={{ textTransform: "none" }}
            children={t("book_location.package_details.next")}
          />
        )}
        {step === BookingCreationStep.bookingSummary && (
          <CustomButton
            isButtonDark={true}
            variant={"outlined"}
            isOutlined={false}
            onClick={createReservation}
            fullWidth={true}
            style={{ textTransform: "none" }}
            children={t("book_location.package_details.reserve")}
          />
        )}
        {!isLastStep && step !== BookingCreationStep.bookingSummary && (
          <BookLocationNavigateNextAction />
        )}
      </ActionsWrapper>
      <LoaderOverlay open={loading} />
      <GeneralDialog
        open={pageDialogOpen}
        onClose={() => setPageDialogOpen(false)}
        onContinue={closePage}
        content={t("use_order.closing_dialog.content")}
      />
    </Root>
  );
};

export default BookLocationPage;
